import { ReactElement, useMemo } from 'react';

import { ReceptionServiceWithPractitionersSimple } from '@dreryk/egabinet-private-medical-service-frontend-api';

import { BindingCreator, createTranslatedText, useTranslate } from '@dreryk/react-common';
import { FilterPanel, Multiselect } from '@dreryk/react-components';

import {
   WaitlistFilterPanelFormBean,
   WaitlistFilterPanelSearchParam,
   WaitlistFilterPanelStorageKey,
} from '../../WaitlistFilterPanel.types';
import { translationKeys } from '../../../translations';

import {
   createServicesCodec,
   creatServicesTriggerLabelExtractor,
   receptionServiceWithPractitionerSearchCreator,
   servicesDeselectorsPropsMapper,
} from './ServicesController.utils';
import { StyledServicesMultiselect } from './ServicesMultiselect.styled';
import { NoServiceItem, ServicesControllerItem } from './ServicesController.types';

export const NO_SERVICE_ITEM: NoServiceItem = 'NO_SERVICE';

type PractitionersControllerProps = {
   registerController: BindingCreator<WaitlistFilterPanelFormBean, 'services'>;
   services: ReceptionServiceWithPractitionersSimple[];
};

export const ServicesController = (props: PractitionersControllerProps): ReactElement => {
   const translate = useTranslate();
   const codec = useMemo(() => createServicesCodec(props.services), [props.services]);
   return (
      <FilterPanel.Controller<WaitlistFilterPanelFormBean, 'services'>
         name={'services'}
         searchParam={WaitlistFilterPanelSearchParam.SERVICES}
         storageKey={WaitlistFilterPanelStorageKey.SERVICES}
         codec={codec}
         chipsLabel={translationKeys.service}
         getDeselectorsProps={servicesDeselectorsPropsMapper}
      >
         {({ applyFilter }) => (
            <StyledServicesMultiselect<ServicesControllerItem>
               testId={'services'}
               triggerLabelExtractor={creatServicesTriggerLabelExtractor(props.services.length, translate)}
               controller={props.registerController('services')}
               onHide={applyFilter}
               label={translationKeys.service}
               placeholder={translationKeys.chooseServices}
               itemKeyGenerator={(item: ReceptionServiceWithPractitionersSimple | NoServiceItem) =>
                  item === NO_SERVICE_ITEM ? item : item.id.asNumber()
               }
               compact
            >
               <Multiselect.SearchCreator<ReceptionServiceWithPractitionersSimple>
                  searchCreator={receptionServiceWithPractitionerSearchCreator}
                  selectAllCheckboxRenderer={(items) => (
                     <Multiselect.SelectAll<ServicesControllerItem>
                        testId={'selectAll'}
                        items={[NO_SERVICE_ITEM, ...(items ?? [])]}
                        searchInputEnabled
                     />
                  )}
               >
                  {({ items }) => (
                     <>
                        <Multiselect.Item
                           item={NO_SERVICE_ITEM}
                           testId={'noService'}
                           label={translationKeys.noService}
                        />
                        <Multiselect.List<ReceptionServiceWithPractitionersSimple> items={items} keyExtractor={'id'}>
                           {(item) => (
                              <Multiselect.Item
                                 item={item}
                                 label={createTranslatedText(item.name)}
                                 testId={item.id.asNumber()}
                              />
                           )}
                        </Multiselect.List>
                     </>
                  )}
               </Multiselect.SearchCreator>
            </StyledServicesMultiselect>
         )}
      </FilterPanel.Controller>
   );
};
